import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { shopifyFetch, COLLECTIONS_QUERY, nodes } from "@/lib/shopify";
import { Reveal, Eyebrow } from "@/components/Reveal";

const BENTO = [
  "md:col-span-2 md:row-span-2 aspect-square",
  "md:col-span-2 aspect-[2/1]",
  "md:col-span-1 aspect-square",
  "md:col-span-1 aspect-square",
  "md:col-span-2 aspect-[2/1]",
  "md:col-span-2 aspect-[2/1]",
];

const PREFERRED_COVER = { "weird-clocks": "vinyl-record-clock-with-stand" };

export default function CategoryGrid() {
  const [collections, setCollections] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    shopifyFetch(COLLECTIONS_QUERY, { first: 10 })
      .then((d) => {
        setCollections(nodes(d.collections).filter((c) => c.handle !== "frontpage"));
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  if (loaded && collections.length === 0) return null;

  return (
    <section data-testid="category-grid-section" className="bg-void py-20 sm:py-28">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <Reveal>
          <Eyebrow>01 — Shop by category</Eyebrow>
          <h2 className="mb-12 font-display text-2xl font-extrabold tracking-tight text-ivory sm:text-3xl lg:text-4xl">
            Pick your <span className="font-editorial italic text-amber">poison</span>
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          {collections.slice(0, 6).map((c, i) => {
            const candidates = (c.products?.edges || []).map((e) => e.node);
            const pick =
              candidates.find((p) => p.handle === PREFERRED_COVER[c.handle]) ||
              candidates.find((p) => !/dog|puppy|dachshund|labrador/i.test(p.handle)) ||
              candidates[0];
            const img = c.image?.url || pick?.featuredImage?.url;
            return (
              <Reveal key={c.id} delay={i * 0.07} className={BENTO[i % BENTO.length]}>
                <Link
                  to={`/collections/${c.handle}`}
                  data-testid={`category-card-${c.handle}`}
                  className="group relative block h-full w-full overflow-hidden rounded-xl border border-white/10"
                >
                  {img && (
                    <img
                      src={img}
                      alt={c.image?.altText || c.title}
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-void/90 via-void/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-5">
                    <div>
                      <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-amber">
                        Collection
                      </p>
                      <h3 className="mt-1 font-display text-xl font-extrabold text-ivory sm:text-2xl">
                        {c.title}
                      </h3>
                    </div>
                    <span className="flex h-10 w-10 items-center justify-center rounded-full border border-ivory/30 text-ivory transition-all duration-300 group-hover:border-amber group-hover:bg-amber group-hover:text-void">
                      <ArrowUpRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
